import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { OrganizationRoleSchema } from './organization-members';
import { SiteAddressSchema } from './site';
const c = initContract();
/**
 * Site as embedded in organization payloads. `externalSiteId` is optional:
 * the tenant-scoped read omits it, the admin reads include it (vendor ids
 * in these contracts are tracked for removal, chantier 7).
 */
export const OrganizationSiteSchema = z.object({
    id: z.string(),
    name: z.string(),
    address: SiteAddressSchema,
    organizationId: z.string(),
    externalSiteId: z.string().optional(),
});
export const OrganizationSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    logo: z.string().nullish(),
    tenantId: z.string().optional(),
    tenantSlug: z.string().nullish(),
    externalOrganizationId: z.string().nullish(),
    totalSitesCount: z.number().optional(),
    sites: z.array(OrganizationSiteSchema),
});
/**
 * Shape of the app-shell read (`getCurrentOrganization`). `myRole` is the
 * caller's role in this organization — non-nullable by construction: a
 * non-member never reaches the 200 (404 from the access check), and a
 * platform super-admin is reported as ADMIN explicitly.
 */
export const CurrentOrganizationSchema = OrganizationSchema.extend({
    myRole: OrganizationRoleSchema,
    /**
     * Deprecated, always true: international eSIMs are open to every
     * organization. Still sent for bundles loaded before the flag removal;
     * drop together with the column.
     */
    travelEsimEnabled: z.boolean(),
});
export const OrganizationSwitcherItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    logo: z.string().nullish(),
    tenantSlug: z.string(),
});
/** Everything an organization takes with it when archived. */
export const OrganizationDeletionImpactSchema = z.object({
    organizationId: z.string(),
    name: z.string(),
    sites: z.number(),
    mobileLines: z.number(),
    /** Lines not terminated yet: still billed by the operator after archiving. */
    activeMobileLines: z.number(),
    members: z.number(),
    pendingInvitations: z.number(),
    orders: z.number(),
    invoices: z.number(),
    checkouts: z.number(),
    customPlans: z.number(),
    consumptionAlerts: z.number(),
});
export const UpdateOrganizationBodySchema = z.object({
    name: z.string().optional(),
    description: z.string().optional(),
    logo: z.string().optional(),
});
export const SyncOrganizationResultSchema = z.object({
    organizationNameUpdated: z.boolean(),
    sitesUpdated: z.number(),
    sitesCreated: z.number(),
});
export const CreateOrganizationBodySchema = z.object({
    name: z.string().min(1),
    tenantSlug: z.string().min(1),
    externalOrganizationId: z.string().min(1),
    sites: z
        .array(z.object({
        name: z.string().min(1),
        externalSiteId: z.string().min(1),
        address: z.object({
            street: z.string().min(1),
            zipCode: z.string().min(1),
            city: z.string().min(1),
            country: z.string().min(1),
        }),
    }))
        .min(1),
});
export const ArchiveOrganizationBodySchema = z.object({
    /** Organization name retyped by the user in the confirmation dialog. */
    confirmationName: z.string().min(1),
});
/**
 * Raw provider organization surfaced by the onboarding screen (id = vendor id).
 * Honest shape: this endpoint proxies the provider, not a local Organization.
 */
export const NotOnboardedOrganizationSchema = z.object({
    id: z.string(),
    name: z.string(),
    sites: z.array(z.object({
        id: z.string(),
        name: z.string(),
        address: z
            .object({
            street: z.string().nullish(),
            zipCode: z.string().nullish(),
            city: z.string().nullish(),
            country: z.string().nullish(),
        })
            .nullish(),
    })),
});
export const organizationContract = c.router({
    /** Tenant-scoped read used by the app shell (site-filtered for non-admins). */
    getCurrentOrganization: {
        method: 'GET',
        path: '/organizations/tenant/:tenantSlug',
        pathParams: z.object({ tenantSlug: z.string().min(1) }),
        responses: { 200: CurrentOrganizationSchema },
        summary: 'Organization of a tenant, sites filtered by access',
    },
    getOrganization: {
        method: 'GET',
        path: '/organization/:organizationId',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: { 200: OrganizationSchema },
        summary: 'Get an organization by id',
    },
    updateOrganization: {
        method: 'PUT',
        path: '/organization/:organizationId',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        body: UpdateOrganizationBodySchema,
        responses: { 200: OrganizationSchema },
        summary: 'Update the organization identity (name, description, logo)',
    },
    syncOrganizationFromSewan: {
        method: 'POST',
        path: '/organization/:organizationId/sync',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        body: c.noBody(),
        responses: { 201: SyncOrganizationResultSchema },
        summary: 'Pull organization name and site details from Sewan',
    },
    listSwitcherOrganizations: {
        method: 'GET',
        path: '/me/organizations',
        responses: { 200: z.array(OrganizationSwitcherItemSchema) },
        summary: 'Organizations of the current user (switcher)',
    },
    listOrganizations: {
        method: 'GET',
        path: '/organizations',
        responses: { 200: z.array(OrganizationSchema) },
        summary: 'List every organization (admin)',
    },
    listNotOnboardedOrganizations: {
        method: 'GET',
        path: '/organizations/not-onboarded',
        responses: { 200: z.array(NotOnboardedOrganizationSchema) },
        summary: 'Provider organizations without an onboarded tenant',
    },
    createOrganization: {
        method: 'POST',
        path: '/organizations',
        body: CreateOrganizationBodySchema,
        responses: { 201: z.object({ success: z.boolean() }) },
        summary: 'Create an organization with its sites',
    },
    getDeletionImpact: {
        method: 'GET',
        path: '/organizations/:organizationId/deletion-impact',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: { 200: OrganizationDeletionImpactSchema },
        summary: 'Everything the organization takes with it, shown before confirming',
    },
    archiveOrganization: {
        method: 'DELETE',
        path: '/organizations/:organizationId',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        body: ArchiveOrganizationBodySchema,
        responses: {
            200: z.object({
                organizationId: z.string(),
                name: z.string(),
                archived: z.boolean(),
            }),
        },
        summary: 'Archive an organization (admin, name confirmation)',
    },
});
